import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { ActionMenuView, HeaderView, LocalizedLink } from "@slfinrtl/ui-bff-routes"
import { getActionMenuViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { openListing } from "src/common/openListing/openListing"
import { addReferralToUrl } from "src/common/referralLink/addReferralToUrl"
import { trackEvent } from "src/components/adobeAnalytics"
import { useModal } from "src/context/modal/useModal"
import type { ActionItemKey, OpenListingModalProps } from "src/hooks"
import { OpenListingModal, useActionMenu, useBff, trackingActionMap } from "src/hooks"
import type { VehicleDetailsStageLinkState } from "../common/VehicleDetailsStageLink"
import { useVehicleDetailsContext } from "../context/VehicleDetailsContext"
import { Header } from "./components/Header"
import { HeaderSkeleton } from "./components/HeaderSkeleton"

interface HeaderContainerProps {
	onEELClick: () => Promise<void>
	onMonroneyLabelClick: () => Promise<void>
	view?: HeaderView
}

export const HeaderContainer = (props: HeaderContainerProps) => {
	const { view } = props

	const [actionMenuView, setActionMenuView] = useState<ActionMenuView | null>()

	const { inventoryId, subscribe } = useVehicleDetailsContext()

	const fetchActionMenu = useBff(
		getActionMenuViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const fetchActionMenuAndSetState = useCallback(
		() =>
			fetchActionMenu()
				.then(setActionMenuView)
				.catch(() => setActionMenuView(null)),
		[fetchActionMenu, setActionMenuView],
	)

	useEffect(() => {
		fetchActionMenuAndSetState()
	}, [fetchActionMenuAndSetState])

	useEffect(() => {
		subscribe("inventoryInformationUpdated", fetchActionMenuAndSetState)
		subscribe("saleCancelled", () => setTimeout(fetchActionMenuAndSetState, 1000))
	}, [subscribe, fetchActionMenuAndSetState])

	return view === undefined || actionMenuView === undefined ? (
		<HeaderSkeleton />
	) : actionMenuView === null ? null : (
		<InnerHeaderContainer {...props} actionMenuView={actionMenuView} view={view} />
	)
}

interface InnerHeaderContainerProps extends HeaderContainerProps {
	actionMenuView: ActionMenuView
	view: HeaderView
}

const InnerHeaderContainer = (props: InnerHeaderContainerProps) => {
	const { actionMenuView, onEELClick, onMonroneyLabelClick, view } = props
	const { getReferralId } = useDealerContext()

	const [customerPrice, setCustomerPrice] = useState<string | null>()
	const [inventoryNumber, setInventoryNumber] = useState<string | null>()

	const { inventoryId, subscribe } = useVehicleDetailsContext()
	const navigate = useNavigate()

	const showOpenListingModal = useModal<OpenListingModalProps>(OpenListingModal)

	useEffect(() => {
		subscribe("inventoryInformationViewChanged", (inventoryInformation) => {
			setCustomerPrice(inventoryInformation === null ? null : inventoryInformation.pricing.customerPrice.value)
			setInventoryNumber(
				inventoryInformation === null ? null : inventoryInformation.inventoryAndIntake.inventoryNumber,
			)
		})
	}, [subscribe])

	const onOpenListing = useCallback(() => {
		trackEvent({ action: "click_on_open_listing_top", category: "outlink" })
	}, [])

	const onOpenListingClick = useCallback(
		async (links: LocalizedLink[]) => {
			const referralId = await getReferralId()

			const linksWithUrl = links.map((link) => ({
				...link,
				url: addReferralToUrl(link.url, { int_id: "dealer_open_listing_referral_link", referral: referralId }),
			}))

			openListing(linksWithUrl, showOpenListingModal, onOpenListing)
		},
		[getReferralId, onOpenListing, showOpenListingModal],
	)

	const onItemClick = useCallback((itemKey: ActionItemKey) => {
		trackEvent({ action: trackingActionMap[itemKey], category: "vehicle_details_action_menu" })
	}, [])

	const handleShowCustomerFeedbackClick = useCallback(() => {
		navigate(
			{},
			{
				preventScrollReset: true,
				replace: true,
				state: { editStage: "customerFeedback", scrollInstantly: true } satisfies VehicleDetailsStageLinkState,
			},
		)
	}, [navigate])

	const actionMenu = useActionMenu({
		actionMenu: actionMenuView,
		hideEditPricing: true,
		onEELClick,
		onItemClick,
		onMonroneyLabelClick,
		vehicleKey: inventoryId,
	})

	return (
		<Header
			inventoryId={inventoryId}
			onShowCustomerFeedbackClick={handleShowCustomerFeedbackClick}
			view={view}
			actionMenu={actionMenu}
			inventoryNumber={inventoryNumber}
			customerPrice={customerPrice}
			onOpenListingClick={onOpenListingClick}
		/>
	)
}
